import { makeObservable } from 'mobx';

import type { Attributes, TConstraints } from '@feathr/rachis';

import { type Template, TemplateClass, type Templates } from '../templates';
import type { Campaign } from './campaign';
import { EmailBaseCampaign } from './email_base';
import type { ISingularEmailBaseCampaign } from './types';
import { CampaignClass } from './types';

export abstract class SingularEmailBaseCampaign<
  T extends ISingularEmailBaseCampaign = ISingularEmailBaseCampaign,
> extends EmailBaseCampaign<T> {
  public override get constraints(): TConstraints<T> {
    return {
      ...super.constraints,
      from_address: {
        presence: {
          allowEmpty: false,
          message: '^A from email address must be provided',
        },
        email: {
          message: '^From email address is not valid',
        },
      },
      from_name: {
        presence: {
          allowEmpty: false,
          message: "^From Name can't be blank.",
        },
        latinCharacters: true,
      },
      template_id: {
        presence: { allowEmpty: false, message: '^Choose a template' },
      },
    };
  }

  constructor(attributes: Partial<T>) {
    super(attributes);

    makeObservable(this);
  }

  public async setTemplate(template: Template): Promise<this> {
    if (!template.id) {
      throw new Error('Template is missing an id');
    }

    const type = template.get('_cls');

    if (type !== TemplateClass.SESEmail) {
      throw new Error('Template is not an Email Template');
    }

    this.set({ template_id: template.id } as Partial<Attributes<this>>);

    if (this.isEphemeral) {
      return this;
    }
    return this.patchDirty();
  }

  public removeTemplate(): void {
    this.set({ template_id: undefined } as Partial<Attributes<this>>);
    this.save();
  }

  /**
   * Builds the polo preview URL for this campaign's email. Pass `templateId` to preview a specific
   * template (e.g. a drip step's own template rather than the campaign's), and `mailingId` to scope
   * the preview to a specific embedded mailing (a drip step — its `step_id` is its `mailing_id`).
   * With no options it previews the campaign's own template, preserving the original behavior.
   */
  public getTemplatePreviewUrl(options?: {
    templateId?: string;
    mailingId?: string;
  }): string | undefined {
    const templateId = options?.templateId ?? this.get('template_id');
    // No template yet (e.g. a draft with no template attached): return undefined
    // rather than a URL containing the literal `t_id=undefined`, which polo 500s.
    // `useEmailPreview` / `EmailPreview` both no-op on an undefined url.
    if (!templateId) {
      return undefined;
    }
    const url = `${POLO_URL}view_email?cpn_id=${this.get('id')}&t_id=${templateId}&preview=1`;
    return options?.mailingId ? `${url}&mailing_id=${options.mailingId}` : url;
  }

  public getTemplate(TemplatesCollection: Templates): Template | undefined {
    const templateId = this.get('template_id');
    return templateId ? TemplatesCollection.get(templateId) : undefined;
  }

  public getTemplates(TemplatesCollection: Templates): Template[] {
    const template = this.getTemplate(TemplatesCollection);
    return template ? [template] : [];
  }
}

// Function that allows type narrowing for Email Campaigns.
export function isSingularEmailCampaign(campaign: Campaign): campaign is SingularEmailBaseCampaign {
  return [
    CampaignClass.AutoPinpointEmail,
    CampaignClass.DripStep,
    CampaignClass.MailrailAutoSend,
    CampaignClass.MailrailSingleSend,
    CampaignClass.MailrailPartnerMessage,
    CampaignClass.PinpointEmail,
    // Difference: includes Partner Message
    CampaignClass.PinpointPartnerMessage,
    CampaignClass.SmartPinpointEmail,
  ].includes(campaign.get('_cls'));
}
